import React from "react";
import { Box, Button, Checkbox, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { scopes } from "data/authData";

function ScopedAccessEditor({ flatRoles, scopedRoles, onChange, disabled }) {
	const handleClearScope = (scopeKey) => {
		const nextScopedRoles = { ...scopedRoles };
		delete nextScopedRoles[scopeKey];
		onChange(nextScopedRoles);
	};

	const handleScopeActionToggle = (scopeKey, actionKey, optionKey) => {
		const nextScopedRoles = { ...scopedRoles };
		const currentScope = { ...(nextScopedRoles[scopeKey] || {}) };
		const currentValues = currentScope[actionKey] || [];
		const hasValue = currentValues.includes(optionKey);
		const nextValues = hasValue ? currentValues.filter((value) => value !== optionKey) : [...currentValues, optionKey];

		if (nextValues.length) currentScope[actionKey] = nextValues;
		else delete currentScope[actionKey];

		if (Object.keys(currentScope).length) nextScopedRoles[scopeKey] = currentScope;
		else delete nextScopedRoles[scopeKey];

		onChange(nextScopedRoles);
	};

	const activeScopes = Object.values(scopes).filter((scope) => flatRoles?.[scope.resource] === true);

	if (!activeScopes.length) return null;

	return (
		<Stack spacing={2} pt={3}>
			<Typography variant="h6">Scoped Access</Typography>
			{activeScopes.map((scope) => {
				const scopeValue = scopedRoles?.[scope.key] || {};
				const options = scope.options || [];

				return (
					<Box
						key={scope.key}
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							overflow: "hidden",
						}}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", backgroundColor: "grey.50" }}>
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
								<Typography variant="subtitle1">{scope.name}</Typography>
								<Chip label={scope.resource} size="small" variant="outlined" />
							</Stack>
							<Button size="small" color="inherit" disabled={disabled || !Object.keys(scopeValue).length} onClick={() => handleClearScope(scope.key)}>
								Clear
							</Button>
						</Stack>

						{options.length ? (
							<Box sx={{ overflowX: "auto" }}>
								<Table
									size="small"
									sx={{
										minWidth: 420,
										"& .MuiTableCell-root": {
											py: 0.5,
											px: 1.5,
										},
									}}>
									<TableHead>
										<TableRow sx={{ backgroundColor: "action.hover" }}>
											<TableCell sx={{ minWidth: 140, fontWeight: 600 }}>Option</TableCell>
											{scope.actions.map((action) => (
												<TableCell key={action} align="center" sx={{ minWidth: 72, fontWeight: 600, textTransform: "uppercase" }}>
													{action}
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{options.map((option) => (
											<TableRow key={option.key} hover>
												<TableCell>
													<Typography variant="body2">{option.name}</Typography>
												</TableCell>
												{scope.actions.map((action) => {
													const checked = (scopeValue[action] || []).includes(option.key);

													return (
														<TableCell key={action} align="center">
															<Checkbox
																size="small"
																checked={checked}
																disabled={disabled}
																onChange={() => handleScopeActionToggle(scope.key, action, option.key)}
															/>
														</TableCell>
													);
												})}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						) : (
							<Box sx={{ px: 2, py: 1.5 }}>
								<Typography variant="body2" color="text.secondary">
									No options configured.
								</Typography>
							</Box>
						)}
					</Box>
				);
			})}
		</Stack>
	);
}

export default ScopedAccessEditor;
